import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiSealCheckFill, PiCircleDashedBold } from 'react-icons/pi';

export type SourceRelevance = 'cited' | 'related';

/**
 * Deterministic source-relevance badge.
 *
 * We intentionally do NOT surface the Azure semantic reranker score (or a
 * High/Medium/Low tier derived from it): that score is a *relative* ranker, not
 * a calibrated relevance classifier — it over-scores near-misses when the
 * corpus is incomplete, so a "High" label can land on an irrelevant document.
 * The only signal we can stand behind is `is_targeted`: the retriever verified
 * that this source literally contains a citation from the matter (an ECLI, a BW
 * article, a named code). That is a hard, deterministic fact.
 *
 *   is_targeted === true  → "Cited"    (verified — the matter references it)
 *   otherwise             → "Related"  (placeholder — surfaced as supporting, no claim)
 */
const CONFIG: Record<SourceRelevance, { key: string; tip: string; Icon: React.ComponentType<{ className?: string }>; badge: string; solid: string }> = {
  cited: {
    key: 'Cited',
    tip: 'Referenced in your matter',
    Icon: PiSealCheckFill,
    badge: 'bg-success-100 dark:bg-success-900/70 text-success-800 dark:text-success-100',
    solid: 'bg-success-600/90 dark:bg-success-700/90 text-white',
  },
  related: {
    key: 'Related',
    tip: 'Related supporting source',
    Icon: PiCircleDashedBold,
    badge: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    solid: 'bg-gray-500/80 dark:bg-gray-600/80 text-white',
  },
};

interface RelevanceBadgeProps {
  /** True when the retriever verified this source is cited by the matter. */
  isTargeted?: boolean | null;
  /** 'badge' = soft chip (on light cards); 'solid' = strong overlay (on image previews). */
  variant?: 'badge' | 'solid';
  className?: string;
}

/** A colour + icon + label representing whether a source is cited by the matter or merely related. */
const RelevanceBadge: React.FC<RelevanceBadgeProps> = ({
  isTargeted,
  variant = 'badge',
  className = '',
}) => {
  const { t } = useTranslation();
  const cfg = isTargeted ? CONFIG.cited : CONFIG.related;
  const color = variant === 'solid' ? cfg.solid : cfg.badge;
  const Icon = cfg.Icon;
  return (
    <span
      title={t(cfg.tip)}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold shadow-sm ${color} ${className}`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      {t(cfg.key)}
    </span>
  );
};

export default RelevanceBadge;
